import { breakPath, setDefaultStyle } from "apparatus/draw";
import { createApparatus } from "apparatus/library";
import { P, Pp, Segments, SharedGradients } from "apparatus/library/common";
import { t_add } from "i18n_utils";
import { Group, Path, Shape } from "paper";
/** Filter paper stroke color */
const strokeColor = "#cccccc";
/** Used by "folded" and "folded-simple". Lower results in narrower paper. */
let FOLD_DRAW_RATIO = 0.8;
export class FilterFunnel {
    render(appearance) {
        const ratio = 0.8;
        const { funnelSize: height, stemLength: stem, stemDiameter: stemD } = appearance;
        const shape = new Path(Segments([[-height * ratio, -height]], [[0, 0]], [[0, stem]], [[stemD, stem + 5]], [[stemD, 0]], [[stemD + height * ratio, -height]]));
        const hitShape = shape.clone();
        const liquidMask = shape.clone();
        const graphic = breakPath(shape, 3);
        setDefaultStyle(graphic);
        return {
            graphic, hitShape, liquidMask,
            liquidDrainOpening: { start: liquidMask.segments[2].point, end: liquidMask.segments[3].point }
        };
    }
}
FilterFunnel.properties = {
    label: "Filter Funnel",
    canContainLiquids: true,
    drainable: true,
    defaultAppearance: {
        funnelSize: 55,
        stemLength: 45,
        stemDiameter: 10,
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "funnelSize", label: "Funnel size",
            min: 30, max: 90,
        },
        {
            spec: "slider", key: "stemLength", label: "Stem length",
            min: 30, max: 80,
        },
        {
            spec: "slider", key: "stemDiameter", label: "Stem diameter",
            min: 7, max: 35, unit: "mm"
        },
    ],
    actions: [
        {
            type: "add",
            label: () => t_add("filter paper"),
            icon: "plus",
            action(a, p, r) {
                let position = P(0, -12 - a.stemLength / 2 + a.stemDiameter * 0.4).rotate(r).add(Pp(p));
                let diameter = Math.round(1.666 * a.funnelSize) + a.stemDiameter;
                let apparatus = createApparatus("filter paper", position);
                apparatus.rotation = r;
                apparatus.appearance.diameter = diameter;
                return apparatus;
            }
        }
    ]
};
export class FilterPaper {
    render(appearance) {
        let graphic = createGraphic(appearance);
        graphic.scale(appearance.diameter / 90);
        var hitShape = Shape.Rectangle(graphic.bounds);
        return { graphic: graphic, hitShape: hitShape };
    }
}
FilterPaper.properties = {
    label: "Filter paper",
    defaultAppearance: {
        style: "folded",
        folding: "simple",
        diameter: 90.0,
        residue: false,
        residueColor: "#9c735d",
    },
    appearanceSpecs: [
        {
            spec: "radio", key: "style", label: "View and style",
            options: [
                { value: "top", label: "Top view" },
                { value: "folded", label: "Folded, side view" },
                { value: "folded-simple", label: "Simple, side view" },
            ]
        },
        {
            spec: "slider", key: "diameter", label: "Diameter",
            min: 60, max: 150,
        },
        {
            spec: "checkbox", key: "residue", label: "Residue",
        },
        {
            visible: a => a.residue,
            spec: "color", key: "residueColor", label: "Residue color",
        },
    ],
};
export class FlutedFilterPaper {
    render(appearance) {
        let graphic = createGraphic(appearance);
        graphic.scale(appearance.diameter / 90);
        var hitShape = Shape.Rectangle(graphic.bounds);
        return { graphic: graphic, hitShape: hitShape };
    }
}
FlutedFilterPaper.properties = {
    label: "Fluted Filter paper",
    defaultAppearance: Object.assign(Object.assign({}, FilterPaper.properties.defaultAppearance), { folding: "fluted" }),
    appearanceSpecs: FilterPaper.properties.appearanceSpecs
};
function createGraphic(appearance) {
    switch (appearance.style) {
        case "top":
            return function () {
                let graphic = new Group();
                let filterPaper = Shape.Circle(P(0, 0), 45)
                    .withStroke("thinner", strokeColor);
                filterPaper.setRadialGradientFill(SharedGradients.filterPaper);
                graphic.addChild(filterPaper);
                if (appearance.folding == "fluted") {
                    let valleyFold = createValleyFold([[0, 0]], [[5, 45]], [[-5, 45]]);
                    graphic.addChild(valleyFold);
                    let mountainFold = createMountainFold("aa", [[0, 0]], [[5, 45]], [[-5, 45]]);
                    mountainFold.rotate(15, P(0, 0));
                    graphic.addChild(mountainFold);
                    for (var i = 1; i < 12; i++) {
                        valleyFold.clone();
                        valleyFold.rotate(30, P(0, 0));
                        mountainFold.clone();
                        mountainFold.rotate(30, P(0, 0));
                        graphic.addChild(valleyFold);
                        graphic.addChild(mountainFold);
                    }
                }
                if (!appearance.residue) {
                    return graphic;
                }
                // Add residue.
                let residue = new Path(Segments([[10, 10], [-5, -5], [6, 6]], [[-10, 10], [-5, 3], [-3, -8]], [[-10, -10], [7, 9], [-4, -11]], [[10, -10], [4, 4], [15, -5]]));
                residue.closePath();
                residue.fillColor = appearance.residueColor + "aa";
                graphic.addChild(residue);
                return graphic;
            }();
        case "folded":
            let graphic = new Group();
            return function () {
                let filterPaper = new Path(Segments([[0, 45]], [[45 * FOLD_DRAW_RATIO, 0], undefined, [3, -5]], [[-45 * FOLD_DRAW_RATIO, 0], [-3, -5]]));
                filterPaper.close()
                    .withStroke("thinner", strokeColor)
                    .setGradientFill("right-down", SharedGradients.filterPaper);
                let topLine = new Path(Segments([[45 * FOLD_DRAW_RATIO, 0], undefined, [0, 5]], [[-45 * FOLD_DRAW_RATIO, 0], [0, 5]]));
                topLine.strokeColor = strokeColor;
                // Add shading for the inside.
                let insideShading = new Path(Segments([[-36, 0], [0, -5], [0, 5]], [[36, 0], [0, 5], [0, -5]]));
                insideShading.closePath();
                insideShading.fillColor = "#00001028";
                graphic.addChildren([filterPaper, insideShading, topLine]);
                if (appearance.folding == "fluted") {
                    let valley = createValleyFold([[0, 45]], [[-5, -5]], [[5, -5]]);
                    graphic.addChild(valley);
                    let mountain1 = createMountainFold("30", [[0, 45]], [[-5, -4]], [[5, -5]]);
                    mountain1.rotate(8, P(0, 45));
                    graphic.addChild(mountain1);
                    let valley1 = valley.clone();
                    valley1.rotate(17, P(0, 45));
                    valley1.scale(1.0, 1.03, P(0, 45));
                    let valley2 = valley.clone();
                    valley2.rotate(34, P(0, 45));
                    valley2.scale(1.0, 1.19, P(0, 45));
                    let valley_1 = valley1.clone();
                    valley1.scale(-1.0, 1.0, P(0, 45));
                    let valley_2 = valley2.clone();
                    valley2.scale(-1.0, 1.0, P(0, 45));
                    let mountain2 = mountain1.clone();
                    mountain1.rotate(17, P(0, 45));
                    mountain1.scale(1.0, 1.06, P(0, 45));
                    let mountain_1 = mountain1.clone();
                    mountain_1.scale(-1.0, 1.0, P(0, 45));
                    let mountain_2 = mountain2.clone();
                    mountain_2.scale(-1.0, 1.0, P(0, 45));
                    graphic.addChildren([valley1, valley2, valley_1, valley_2, mountain1, mountain2, mountain_1, mountain_2]);
                }
                else {
                    let foldedPart = new Path(Segments([[-45 * FOLD_DRAW_RATIO, 0], undefined, [3, 5]], [[-20 * FOLD_DRAW_RATIO, 8]], [[0, 45]]));
                    foldedPart.strokeColor = strokeColor;
                    graphic.addChild(foldedPart);
                }
                if (!appearance.residue) {
                    return graphic;
                }
                let residue = new Path(Segments([[0, 45]], [[16, 25], undefined, [-8, -8]], [[-16, 25], [8, 8]]));
                residue.closePath();
                residue.fillColor = appearance.residueColor + "60";
                graphic.addChild(residue);
                return graphic;
            }();
        case "folded-simple":
            return foldedSimple(appearance);
    }
}
function foldedSimple(appearance) {
    const graphic = new Group();
    const shape = new Path(Segments([[-45 * FOLD_DRAW_RATIO, 0]], [[0, 45]], [[45 * FOLD_DRAW_RATIO, 0]]));
    setDefaultStyle(shape);
    graphic.addChild(shape);
    if (appearance.residue) {
        let residue = new Path(Segments([[0, 45]], [[16, 25], undefined, [-8, -8]], [[-16, 25], [8, 8]]));
        residue.closePath();
        residue.fillColor = appearance.residueColor + "60";
        graphic.addChild(residue);
    }
    return graphic;
}
function createValleyFold(...segments) {
    let valleyFold = new Path(Segments(...segments));
    valleyFold.setGradientFill("right", [["#00000000", 0.0], ["#00000015", 0.5], ["#00000000", 1.0]]);
    return valleyFold;
}
function createMountainFold(opacity, ...segments) {
    let mountainFold = new Path(Segments(...segments));
    mountainFold.setGradientFill("right", [["#ffffff00", 0.0], ["#ffffff" + opacity, 0.5], ["#ffffff00", 1.0]]);
    return mountainFold;
}
